import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BackgroundColorProps,
  BorderColorProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
  JustifyContentProps,
  SizeProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  justifyContent,
  size,
  space,
  themeGet,
} from 'styled-system';

import { Link } from 'common/components/Link';
import { BodyBoldBB2 } from 'common/components/Typography';
import { focusOutlineCSSWithOffset, textWithEllipsis } from 'common/theme/styles';
import type { SpaceProps } from 'common/theme/system-types';
import type { FlexShrinkProps, TextDecorationProps } from 'common/theme/system-utilities';
import { flexShrink, textDecoration } from 'common/theme/system-utilities';
import type { VisualNavigationItemData } from 'common/types/visual-navigation';
import SlideImage from './SlideImage';

interface VerticalCategoryItemProps extends VisualNavigationItemData {
  index: number;
  onClick: () => void;
}

const THUMBNAIL_SIZE = [40, 48, 48, 64];

const UnstyledItemLink = styled(Link)<
  BorderColorProps &
    BorderRadiusProps &
    BorderWidthProps &
    SpaceProps &
    BackgroundColorProps &
    BorderStyleProps &
    DisplayProps &
    AlignItemsProps &
    JustifyContentProps &
    TextDecorationProps
>`
  box-sizing: border-box;

  ${display};
  ${alignItems};
  ${justifyContent};
  ${textDecoration};
  ${borderColor};
  ${borderRadius};
  ${borderWidth};
  ${borderStyle};
  ${space};
  ${backgroundColor};

  &:hover {
    text-decoration: none;
    border-color: ${themeGet('colors.sys.neutral.border.hover')};
  }

  &:active {
    box-shadow: inset 0 0 0 2px ${themeGet('colors.sys.neutral.border.hover')};
    background-color: ${themeGet('colors.sys.neutral.background.active')};
  }

  ${focusOutlineCSSWithOffset};

  &:focus-visible {
    outline-offset: 0px;
  }
`;

const itemLinkStyles: ComponentProps<typeof UnstyledItemLink> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  textDecoration: 'none',
  borderRadius: 'medium',
  borderWidth: 'small',
  borderStyle: 'solid',
  borderColor: 'transparent',
  py: ['sp_8', null, null, 'sp_4'],
  pl: ['sp_8', 'sp_12'],
  pr: ['sp_8', null, null, 'sp_4'],
  backgroundColor: 'sys.neutral.background.hover',
};

const ItemLink = (props: ComponentProps<typeof UnstyledItemLink>) => (
  <UnstyledItemLink {...itemLinkStyles} {...props} />
);

const TruncatedText = styled(BodyBoldBB2)`
  ${textWithEllipsis(2)};
`;

const UnstyledImageContainer = styled.div<SizeProps & FlexShrinkProps>`
  ${size};
  ${flexShrink};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ImageContainer = (props: ComponentProps<typeof UnstyledImageContainer>) => (
  <UnstyledImageContainer flexShrink={0} {...props} />
);

export const VerticalCategoryItem = ({
  link,
  description,
  image: imageProps,
  index,
  onClick,
}: VerticalCategoryItemProps) => (
  <ItemLink href={link} onClick={onClick} data-testid={`vertical-category-item-${index}`}>
    <TruncatedText as="span" data-testid="vertical-category-item-description">
      {description}
    </TruncatedText>
    {imageProps && (
      <ImageContainer size={THUMBNAIL_SIZE}>
        <SlideImage imageProps={imageProps} index={index} alt={description || ''} />
      </ImageContainer>
    )}
  </ItemLink>
);
